import styled from '@emotion/styled';
import { Box, Stack } from '@mui/material';
import ProfileContainer from 'components/Account/ProfileContainer';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import InfoModal from 'components/Common/InfoModal';
import TextInput from 'components/Common/TextInput';
import { updateMerchantInfo } from 'redux/slices/getMoneyContainerSlice';
import { notify } from 'components/Common/Toast';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useState } from 'react';
import ProfileItemDetail from 'components/ProfileItemDetail';
import Button from 'components/Common/CustomButton';
import { getFullName } from 'utils/helpers';

const Container = styled('div')({
    backgroundColor: 'white',
    padding: '20px', // Add consistent padding to the main container
    minHeight: '60vh', // Ensure a minimum height to cover the viewport
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});
const formSchema = yup.object({
    companyName: yup.string().required('Business name is required.'),
});

const AccountDetailScreen = () => {
    const userInfo = useSelector((state: RootState) => state.user?.userInfo);
    const { firstName, lastName, image, email } = userInfo || {};

    let navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const {
        handleSubmit,
        getValues,
        control,
        reset,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(formSchema),
        defaultValues: { companyName: '' },
    });

    const navigationHandler = () => {
        navigate(routes.account.changePhoto);
    };

    const handelClickConfirm = async () => {
        const { companyName } = getValues();
        setLoading(true);
        const response: any = await dispatch(updateMerchantInfo(companyName));
        const { code } = response?.payload || {};
        if (code === 200) {
            notify('Business name updated successfully', 'success');
            setModalOpen(false);
            reset();
        }
        setLoading(false);
    };
    const handelCancel = () => {
        setModalOpen(false);
        reset();
    };

    return (
        <Container>
            <Stack direction="row" sx={{ alignItems: 'stretch', px: 10 }}>
                <Box sx={{ padding: 2 }}>
                    <ProfileContainer navigationHandler={navigationHandler} imageUrl={image} businessAvatar />
                </Box>
                <Box sx={{ flex: 1, padding: 2, mt: 2 }}>
                    <ProfileItemDetail fontWeight={'600'} label={getFullName({ firstName, lastName })} value={email} />
                </Box>
            </Stack>
            <Stack alignItems={'center'} mb={5}>
                <Button
                    onClick={() => navigate(routes.account.changePassword, { state: { type: 'resetPassword' } })}
                    sx={{ width: { xs: '90%', sm: '40%' } }}
                    title="Reset Password"
                />
            </Stack>
            <InfoModal
                loading={loading}
                description="Are you sure you want to change the business name?"
                isOpen={modalOpen}
                onClose={handelCancel}
                title={'Update Business Name'}
                onConfirm={handleSubmit(handelClickConfirm)}
            >
                <Controller
                    name="companyName"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            {...field}
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Business Name"
                            icon={null}
                            error={!!errors?.companyName}
                            helperText={(errors.companyName as any)?.message}
                        />
                    )}
                />
            </InfoModal>
        </Container>
    );
};

export default AccountDetailScreen;
